import { useEffect, useState } from 'react';
import './HomePage.css';
import { getDashboard, getCurrencyRate } from '../../api/dashboardAPI/dashboardAPI.js';
import { listSessions, revokeSession, logout, subscribeLogs } from '../../api/authAPI/authAPI.js';
import Loader from '../../components/Loader/Loader.jsx';
import useLang from '../../hooks/use-lang/useLang.js';

const EMPTY_DASHBOARD = {
  total_revenue: 0,
  hetzner_cost: 0,
  total_margin: 0,
  active_servers: 0,
  total_users: 0,
  monthly_revenue: 0,
  recent_transactions: [],
  recent_users: [],
  server_statuses: [],
  low_balance: [],
  recent_plans: [],
};

function parseBrowser(userAgent) {
  if (!userAgent) return 'Unknown';
  if (userAgent.includes('Edg')) return 'Edge';
  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Safari')) return 'Safari';
  return 'Browser';
}

function parseOS(userAgent) {
  if (!userAgent) return 'Unknown';
  if (userAgent.includes('Windows NT 10')) return 'Windows 10/11';
  if (userAgent.includes('Windows NT 6.3')) return 'Windows 8.1';
  if (userAgent.includes('Windows NT 6.2')) return 'Windows 8';
  if (userAgent.includes('Windows NT 6.1')) return 'Windows 7';
  if (userAgent.includes('Windows')) return 'Windows';
  if (userAgent.includes('Mac OS X 14') || userAgent.includes('Mac OS X 15')) return 'macOS 14+';
  if (userAgent.includes('Mac OS X 13')) return 'macOS 13';
  if (userAgent.includes('Mac OS X 12')) return 'macOS 12';
  if (userAgent.includes('Mac OS X 11')) return 'macOS 11';
  if (userAgent.includes('Mac')) return 'macOS';
  if (userAgent.includes('Android 14')) return 'Android 14';
  if (userAgent.includes('Android 13')) return 'Android 13';
  if (userAgent.includes('Android 12')) return 'Android 12';
  if (userAgent.includes('Android')) return 'Android';
  if (userAgent.includes('iPhone') || userAgent.includes('iOS')) return 'iOS';
  if (userAgent.includes('Linux')) return 'Linux';
  return 'OS';
}

function HomePage({ onNavigate, onLogout }) {
  const { t } = useLang();
  const [sessions, setSessions] = useState([]);
  const [logs, setLogs] = useState([]);
  const [consoleOpen, setConsoleOpen] = useState(false);
  const [metrics, setMetrics] = useState(EMPTY_DASHBOARD);
  const [uahRate, setUahRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revokingId, setRevokingId] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);

    Promise.all([
      getDashboard().catch((err) => { throw err; }),
      listSessions().catch(() => []),
      getCurrencyRate().catch(() => null),
    ])
      .then(([data, sess, rate]) => {
        if (!active) return;
        setMetrics(data);
        setSessions(sess);
        setUahRate(rate);
        setError(null);
      })
      .catch((err) => {
        if (!active) return;
        setError(err.message || 'Failed to load dashboard');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    const unsubscribe = subscribeLogs((log) => {
      if (!active) return;
      setLogs((prev) => [...prev.slice(-99), log]);
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  async function handleDisconnect(sessionId) {
    setRevokingId(sessionId);
    try {
      const result = await revokeSession(sessionId);
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      if (result.current) {
        logout();
        window.location.reload();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setRevokingId(null);
    }
  }

  return (
    <div className="HomePage">
      {loading ? (
        <Loader text={t('dashboard.loading', 'Loading dashboard…')} />
      ) : error ? (
        <div className="dashboard__error">
          <p className="dashboard__error-text">{t('dashboard.loadError', 'Не вдалося завантажити дашборд')}</p>
          <span className="dashboard__error-detail">{error}</span>
          <button className="dashboard__error-retry" type="button" onClick={() => window.location.reload()}>{t('common.retry', 'Повторити')}</button>
        </div>
      ) : (
        <>
      <div className="hero__container">
        <div className="total__revenue__container">
          <h2 className="card__title">{t('dashboard.totalRevenue', 'Total Revenue')}</h2>
          <div className="card__row">
            <p className="card__value">{`$${metrics.total_revenue.toFixed(2)}`}</p>
            {uahRate && <span className="card__uah">{`≈ ${Math.round(metrics.total_revenue * uahRate).toLocaleString('uk-UA')} ₴`}</span>}
          </div>
        </div>

        <div className="hetzer__cost__container">
          <h2 className="card__title">{t('dashboard.hetznerCost', 'Hetzner Cost')}</h2>
          <div className="card__row">
            <p className="card__value">{`$${metrics.hetzner_cost.toFixed(2)}`}</p>
            {uahRate && <span className="card__uah">{`≈ ${Math.round(metrics.hetzner_cost * uahRate).toLocaleString('uk-UA')} ₴`}</span>}
          </div>
        </div>

        <div className="total__margin__container">
          <h2 className="card__title">{t('dashboard.totalMargin', 'Total Margin')}</h2>
          <div className="card__row">
            <p className="card__value">{`$${metrics.total_margin.toFixed(2)}`}</p>
            {uahRate && <span className="card__uah">{`≈ ${Math.round(metrics.total_margin * uahRate).toLocaleString('uk-UA')} ₴`}</span>}
          </div>
        </div>

        <div className="total__active__servers">
          <h2 className="card__title">{t('dashboard.activeServers', 'Active Servers')}</h2>
          <div className="card__row">
            <p className="card__value">{metrics.active_servers}</p>
          </div>
        </div>

        <div className="total__users__container">
          <h2 className="card__title">{t('dashboard.totalUsers', 'Total Users')}</h2>
          <div className="card__row">
            <p className="card__value">{metrics.total_users}</p>
          </div>
        </div>

        <div className="monthly__revenue__container">
          <h2 className="card__title">{t('dashboard.monthlyRevenue', 'Monthly Revenue')}</h2>
          <div className="card__row">
            <p className="card__value">{`$${metrics.monthly_revenue.toFixed(2)}`}</p>
            {uahRate && <span className="card__uah">{`≈ ${Math.round(metrics.monthly_revenue * uahRate).toLocaleString('uk-UA')} ₴`}</span>}
          </div>
        </div>
      </div>

      <div className="recent__actions__container">
        <div className="recent__transactions__container">
          <div className="section__header">
            <h2 className="section__title">{t('dashboard.recentTransactions', 'Recent Transactions')}</h2>
            <button className="show-all-button" type="button" onClick={() => onNavigate('Transactions')}>{t('common.showAll')}</button>
          </div>
          <ul className={metrics.recent_transactions.length === 0 ? 'section__list section__list--empty' : 'section__list'}>
            {metrics.recent_transactions.length === 0 ? (
              <li className="section__empty">{t('dashboard.noTransactions')}</li>
            ) : metrics.recent_transactions.map((tx) => (
              <li key={tx.id} className={`section__item transaction__${tx.type}`}>
                <span className="transaction__type">{tx.userId}</span>
                <span className="transaction__amount">{tx.amount > 0 ? (tx.type === 'deposit' ? '+' : '-') : ''}${tx.amount.toFixed(2)}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="recent__users__container">
          <div className="section__header">
            <h2 className="section__title">{t('dashboard.recentUsers', 'Recent Users')}</h2>
            <button className="show-all-button" type="button" onClick={() => onNavigate('Users')}>{t('common.showAll')}</button>
          </div>
          <ul className={metrics.recent_users.length === 0 ? 'section__list section__list--empty' : 'section__list'}>
            {metrics.recent_users.length === 0 ? (
              <li className="section__empty">{t('dashboard.noUsers')}</li>
            ) : metrics.recent_users.map((user) => (
              <li key={user.id} className="section__item user__item">
                <span className="user__name">{user.username}</span>
                <span className="user__action">{t(`common.${user.status}`, user.status)}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="server__statuses__container">
          <div className="section__header">
            <h2 className="section__title">{t('dashboard.serverStatuses', 'Server Statuses')}</h2>
            <button className="show-all-button" type="button" onClick={() => onNavigate('Active Servers')}>{t('common.showAll')}</button>
          </div>
          <ul className={metrics.server_statuses.length === 0 ? 'section__list section__list--empty' : 'section__list'}>
            {metrics.server_statuses.length === 0 ? (
              <li className="section__empty">{t('dashboard.noServers')}</li>
            ) : metrics.server_statuses.map((item) => (
              <li key={item.status} className={`section__item status__item status__${item.status}`}>
                <span className="status__name">{t(`common.${item.status}`, item.status)}:</span>
                <span className="status__count">{item.count}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="bottom__section__container">
        <div className="low__balance__container">
          <div className="section__header">
            <h2 className="section__title">{t('dashboard.lowBalance', 'Low Balance')}</h2>
            <button className="show-all-button" type="button" onClick={() => onNavigate('Users')}>{t('common.showAll')}</button>
          </div>
          <ul className={metrics.low_balance.length === 0 ? 'section__list section__list--empty' : 'section__list'}>
            {metrics.low_balance.length === 0 ? (
              <li className="section__empty">{t('dashboard.allFunded')}</li>
            ) : metrics.low_balance.map((user) => (
              <li key={user.id} className="section__item balance__item">
                <span className="balance__user">{user.username || user.nickname}</span>
                <span className="balance__amount">
                  ${Number(user.balance).toFixed(2)} / {user.renew_price != null ? `$${Number(user.renew_price).toFixed(2)}` : '—'}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="recent__plans__container">
          <div className="section__header">
            <h2 className="section__title">{t('dashboard.recentPlans', 'Recent Plans')}</h2>
            <button className="show-all-button" type="button" onClick={() => onNavigate('Plans')}>{t('common.showAll')}</button>
          </div>
          <ul className={metrics.recent_plans.length === 0 ? 'section__list section__list--empty' : 'section__list'}>
            {metrics.recent_plans.length === 0 ? (
              <li className="section__empty">{t('dashboard.noPlans')}</li>
            ) : metrics.recent_plans.map((plan) => (
              <li key={plan.id} className="section__item plan__item">
                <span className="plan__user">{plan.name}</span>
                <span className="plan__action">${plan.price.toFixed(2)} · {t(`common.${plan.status}`, plan.status)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="dashboard__monitoring__container">
        <div className="backend__console__container">
          <div className="section__header">
            <h2 className="section__title">{t('dashboard.backendConsole')}</h2>
            <div className="console__header-actions">
              <span className="console__status">{t('dashboard.live')}</span>
              <button className="console__expand-btn" type="button" onClick={() => setConsoleOpen(true)} aria-label={t('dashboard.expandConsole', 'Expand console')}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 3 21 3 21 9" />
                  <polyline points="9 21 3 21 3 15" />
                  <line x1="21" y1="3" x2="14" y2="10" />
                  <line x1="3" y1="21" x2="10" y2="14" />
                </svg>
              </button>
            </div>
          </div>
          <div className="console__output">
            {logs.length === 0 ? (
              <div className="console__line">
                <span className="console__message">{t('dashboard.waitingLogs')}</span>
              </div>
            ) : logs.map((log, index) => (
              <div key={log.id || index} className="console__line">
                <span className="console__time">{new Date(log.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                <span className={`console__level console__level--${log.level}`}>{log.level.toUpperCase()}</span>
                <span className="console__source">[{log.source}]</span>
                <span className="console__message">{log.message}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="active__sessions__container">
          <div className="section__header">
            <h2 className="section__title">{t('nav.activeSessions', 'Active Sessions')}</h2>
            <span className="sessions__count">{t('dashboard.sessionsOnline').replace('{n}', sessions.length)}</span>
          </div>
          <ul className={sessions.length === 0 ? 'section__list section__list--empty' : 'section__list'}>
            {sessions.length === 0 ? (
              <li className="section__empty">{t('dashboard.noActiveSessions')}</li>
            ) : sessions.map((session) => (
              <li key={session.id} className="section__item session__item">
                <div className="session__info">
                  <span className="session__user">{parseBrowser(session.user_agent)} / {parseOS(session.user_agent)}</span>
                  <span className="session__meta">{session.ip || '—'}</span>
                </div>
                <div className="session__actions">
                  <span className="session__badge session__badge--active">{t('common.active')}</span>
                  <button
                    className="session__disconnect"
                    type="button"
                    onClick={() => handleDisconnect(session.id)}
                    disabled={revokingId === session.id}
                  >
                    {revokingId === session.id ? '...' : t('dashboard.disconnect')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {consoleOpen && (
        <div className="console__fullscreen-overlay" onClick={() => setConsoleOpen(false)}>
          <div className="console__fullscreen" onClick={(e) => e.stopPropagation()}>
            <div className="console__fullscreen-header">
              <div className="console__fullscreen-title-row">
                <h2 className="section__title">{t('dashboard.backendConsole')}</h2>
                <span className="console__status">{t('dashboard.live')}</span>
              </div>
              <button className="console__close-btn" type="button" onClick={() => setConsoleOpen(false)} aria-label={t('dashboard.closeConsole', 'Close console')}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="console__fullscreen-output">
              {logs.length === 0 ? (
                <div className="console__line">
                  <span className="console__message">{t('dashboard.waitingLogs')}</span>
                </div>
              ) : logs.map((log, index) => (
                <div key={log.id || index} className="console__line">
                  <span className="console__time">{new Date(log.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                  <span className={`console__level console__level--${log.level}`}>{log.level.toUpperCase()}</span>
                  <span className="console__source">[{log.source}]</span>
                  <span className="console__message">{log.message}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}

export default HomePage;
